import { Time } from "@internationalized/date"
import { updateFetcherData } from "@prismicio/editor-support/Suspense"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  FormDateInput,
  FormInput,
  useAlert
} from "@prismicio/editor-ui"
import { type ReactNode, useState } from "react"

import {
  createRelease as doCreateRelease,
  getAllReleases
} from "../../app/services/release"

interface CreateReleaseDialogProps {
  children: ReactNode
}

export function CreateReleaseDialog(props: CreateReleaseDialogProps) {
  const { children } = props

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [release, setRelease] = useState<NewRelease>({
    label: "",
    date: undefined
  })

  const { showAlert } = useAlert()
  const { disabled, reason } = getOkButtonDisabledProperties({
    release
  })

  function onOkClick() {
    if (!release) return

    setLoading(true)
    createRelease({
      release,
      onSuccess: () => {
        setLoading(false)
        setOpen(false)
        setRelease({ label: "", date: undefined })
      },
      onError: () => {
        setLoading(false)
        setOpen(false)
        showAlert({
          title: `Oops! We were unable to create your release.`,
          subtitle: `We encountered an error while trying to create your release. Please contact our support team if this issue persists.`,
          onRetry: onOkClick
        })
      }
    }).catch(() => undefined)
  }

  return (
    <Dialog trigger={children} size="small" open={open} onOpenChange={setOpen}>
      <DialogHeader icon="calendarToday" title="Add Release" />

      <DialogContent>
        <Box
          as="form"
          flexDirection="column"
          padding={{ inline: 16, top: 16, bottom: 4 }}
          flexGrow={1}
          gap={16}
        >
          <FormInput
            value={release.label}
            label="Release name"
            onValueChange={(value) => setRelease({ ...release, label: value })}
            placeholder="e.g: Website update"
            disabled={loading}
          />
          <FormDateInput
            mode="date-time"
            label="Schedule publication"
            minValue="today"
            value={release?.date}
            onValueChange={(value) => setRelease({ ...release, date: value })}
            defaultTime={new Time(0)}
            disabled={loading}
          />
        </Box>

        <DialogActions
          ok={{
            text: "Add",
            onClick: onOkClick,
            disabled,
            disabledTooltip: reason,
            loading
          }}
          cancel={{ text: "Cancel" }}
        />
      </DialogContent>
    </Dialog>
  )
}

interface NewRelease {
  label: string
  date?: Date
}

interface GetOkButtonDisabledPropertiesArgs {
  release: NewRelease
}

function getOkButtonDisabledProperties(
  props: GetOkButtonDisabledPropertiesArgs
) {
  const { release } = props

  if (!release.label) {
    return { disabled: true, reason: "The release name is required" }
  }

  return { disabled: false }
}

interface CreateReleaseArgs {
  release: NewRelease
  onSuccess: () => void
  onError: () => void
}

async function createRelease(args: CreateReleaseArgs) {
  const { release, onSuccess, onError } = args

  try {
    const createdRelease = await doCreateRelease(release)
    updateFetcherData(getAllReleases, (body) => [...body, createdRelease])
    onSuccess()
  } catch (_) {
    onError()
  }
}
